/**
 * BLOG DOWNGRADE SELECTOR
 *
 * Modal-Dialog zur Auswahl der YouTube-Kanäle, die ein User
 * bei einem Bitcoin-Plan-Downgrade behalten möchte.
 *
 * Strikt getrennt vom FCMS-DowngradeResourceSelector. Nutzt eigene API-Routen
 * (/subscriptions/blog-downgrade/*) und zeigt nur YouTube-Kanäle (kein
 * Asset/Watchlist/Chart-Layout-Konzept im Blog-Bereich).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Check, Youtube, AlertTriangle, ArrowDown, ChevronLeft } from 'lucide-react';
import { TWModal, TWModalFooter, TWModalButton } from '../ui/TWModal';
import {
  getBlogDowngradeResources,
  getBlogDowngradeSelection,
  saveBlogDowngradeSelection,
  getDowngradeJournalImpact,
  type BlogDowngradeResources,
  type JournalImpactPreview as JournalImpactPreviewData,
} from '../../services/blog/blogDowngradeService';
import { twToast } from '../ui/TWToast';
import JournalImpactPreview from '../blog/JournalImpactPreview';

interface BlogDowngradeSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  effectiveDate: string;
}

const getImageUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const isLocal = window.location.hostname === 'localhost';
  const baseUrl = isLocal ? 'http://localhost:5555' : window.location.origin;
  return baseUrl + path;
};

export const BlogDowngradeSelector: React.FC<BlogDowngradeSelectorProps> = ({
  isOpen,
  onClose,
  onSaved,
  effectiveDate,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resources, setResources] = useState<BlogDowngradeResources | null>(null);
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(new Set());

  // Confirm-Step (Aufklärungs-UX)
  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [impact, setImpact] = useState<JournalImpactPreviewData | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);
  const [understood, setUnderstood] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [resourceData, existingSelection] = await Promise.all([
        getBlogDowngradeResources(),
        getBlogDowngradeSelection(),
      ]);

      setResources(resourceData);

      if (existingSelection && existingSelection.status === 'pending') {
        setSelectedChannels(new Set(existingSelection.retainedYoutubeChannelIds));
      } else {
        // Default: Älteste Kanäle bis zum Limit vorauswählen
        const limit = resourceData.youtubeChannelLimit;
        if (limit === null) {
          setSelectedChannels(new Set(resourceData.youtubeChannels.map(c => c.id)));
        } else {
          // Channels sind nach added_at ASC sortiert -> älteste zuerst
          setSelectedChannels(new Set(resourceData.youtubeChannels.slice(0, limit).map(c => c.id)));
        }
      }
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Kanäle');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadData();
      // Beim Wieder-Öffnen Confirm-State zurücksetzen
      setStep('select');
      setImpact(null);
      setUnderstood(false);
    }
  }, [isOpen, loadData]);

  const toggleChannel = (id: string) => {
    setSelectedChannels(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const limit = resources?.youtubeChannelLimit ?? null;
  const selectedCount = selectedChannels.size;
  const hasLimit = limit !== null;
  const overLimit = hasLimit && selectedCount > limit;
  const underLimit = hasLimit && selectedCount < limit;
  const remaining = hasLimit ? limit - selectedCount : 0;

  // Schritt 1 -> 2: Impact-Vorschau laden und zur Confirm-Stufe wechseln.
  const handleProceedToConfirm = async () => {
    if (overLimit) {
      twToast.error(`Du kannst maximal ${limit} Kanäle behalten`);
      return;
    }
    setImpactLoading(true);
    setUnderstood(false);
    try {
      const data = await getDowngradeJournalImpact(Array.from(selectedChannels));
      setImpact(data);
      setStep('confirm');
    } catch (err: any) {
      twToast.error(err.message || 'Vorschau konnte nicht geladen werden');
    } finally {
      setImpactLoading(false);
    }
  };

  // Schritt 2 -> Save: Auswahl persistieren.
  const handleConfirmDowngrade = async () => {
    if (overLimit) return;
    setSaving(true);
    try {
      await saveBlogDowngradeSelection(Array.from(selectedChannels));
      twToast.success('Auswahl gespeichert');
      onSaved();
    } catch (err: any) {
      twToast.error(err.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToSelect = () => {
    setStep('select');
    setUnderstood(false);
  };

  const formatEffectiveDate = (date: string): string => {
    try {
      return new Date(date).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return date;
    }
  };

  // Confirm-Step Daten ableiten
  const retainedChannels = resources?.youtubeChannels.filter(c => selectedChannels.has(c.id)) ?? [];
  const removedChannels = resources?.youtubeChannels.filter(c => !selectedChannels.has(c.id)) ?? [];

  return (
    <TWModal
      isOpen={isOpen}
      onClose={onClose}
      title={step === 'select' ? 'Kanäle auswählen' : 'Downgrade bestätigen'}
      icon={<ArrowDown className="w-5 h-5 text-[#F7931A]" />}
      size="lg"
    >
      {step === 'confirm' ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/30 p-3">
            <p className="text-sm text-orange-700 dark:text-orange-400">
              Dein Plan-Wechsel wird am{' '}
              <strong>{formatEffectiveDate(effectiveDate)}</strong> wirksam.
              Bitte prüfe deine Auswahl.
            </p>
          </div>

          {/* Behalten / Verlieren */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/15 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-2">
                Du behältst ({retainedChannels.length})
              </p>
              <ul className="space-y-1">
                {retainedChannels.map(c => (
                  <li key={c.id} className="text-sm text-emerald-900 dark:text-emerald-100 truncate">
                    · {c.channelTitle}
                  </li>
                ))}
                {retainedChannels.length === 0 && (
                  <li className="text-xs italic text-emerald-700 dark:text-emerald-300">Keine Kanäle behalten</li>
                )}
              </ul>
            </div>
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/15 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-red-700 dark:text-red-300 mb-2">
                Du verlierst ({removedChannels.length})
              </p>
              <ul className="space-y-1">
                {removedChannels.map(c => (
                  <li key={c.id} className="text-sm text-red-900 dark:text-red-100 truncate">
                    · {c.channelTitle}
                  </li>
                ))}
                {removedChannels.length === 0 && (
                  <li className="text-xs italic text-red-700 dark:text-red-300">Keine Kanäle verloren</li>
                )}
              </ul>
            </div>
          </div>

          {/* Journal-Impact-Vorschau */}
          <JournalImpactPreview
            impact={impact}
            loading={impactLoading}
            variant="downgrade"
          />

          {/* Verstehens-Checkbox */}
          {impact && impact.affectedEntryCount > 0 && (
            <label className="flex items-start gap-2 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 cursor-pointer">
              <input
                type="checkbox"
                checked={understood}
                onChange={(e) => setUnderstood(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-brand"
              />
              <span className="text-sm text-gray-800 dark:text-gray-200">
                Ich habe verstanden, dass die Verlinkungen nach dem Downgrade gekappt werden
                und nicht automatisch zurückkehren. Meine Notiz-Texte bleiben erhalten.
              </span>
            </label>
          )}
        </div>
      ) : (
      <div className="space-y-4">
        {/* Hinweis zum Wechsel-Datum */}
        <div className="rounded-lg bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/30 p-3">
          <p className="text-sm text-orange-700 dark:text-orange-400">
            Dein Plan-Wechsel wird am{' '}
            <strong>{formatEffectiveDate(effectiveDate)}</strong> wirksam.
            Nicht ausgewählte Kanäle werden zu diesem Zeitpunkt unwiderruflich aus deinem Newsfeed entfernt.
          </p>
        </div>

        {error && (
          <div className="rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-brand" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Kanäle werden geladen...</p>
          </div>
        ) : resources ? (
          <>
            {/* Limit-Anzeige */}
            <div className="flex items-center justify-between rounded-lg bg-gray-50 dark:bg-gray-800 px-4 py-3">
              <div className="flex items-center gap-2">
                <Youtube className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  YouTube-Kanäle
                </span>
              </div>
              <div className="text-sm">
                {hasLimit ? (
                  <span className={overLimit ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-700 dark:text-gray-300'}>
                    {selectedCount} / {limit} ausgewählt
                  </span>
                ) : (
                  <span className="text-gray-700 dark:text-gray-300">
                    {selectedCount} ausgewählt (unbegrenzt)
                  </span>
                )}
              </div>
            </div>

            {underLimit && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Du kannst noch {remaining} weitere{remaining === 1 ? 'n' : ''} Kanal {remaining === 1 ? '' : 'e '}auswählen.
              </p>
            )}

            {overLimit && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 p-3">
                <p className="text-sm text-red-700 dark:text-red-400">
                  Du hast {selectedCount} Kanäle ausgewählt, aber dein neuer Plan erlaubt maximal {limit}.
                  Bitte entferne {selectedCount - (limit ?? 0)} Kanal{selectedCount - (limit ?? 0) === 1 ? '' : 'e'} aus der Auswahl.
                </p>
              </div>
            )}

            {/* Kanal-Liste */}
            {resources.youtubeChannels.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Youtube className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Du hast keine YouTube-Kanäle hinterlegt.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
                {resources.youtubeChannels.map(channel => {
                  const isSelected = selectedChannels.has(channel.id);
                  return (
                    <button
                      key={channel.id}
                      type="button"
                      onClick={() => toggleChannel(channel.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                        isSelected
                          ? 'bg-[#F7931A]/10 border-[#F7931A] dark:bg-[#F7931A]/15'
                          : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                    >
                      {/* Checkbox-Indikator */}
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 transition-colors ${
                        isSelected
                          ? 'bg-[#F7931A] text-white'
                          : 'bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-300 dark:ring-gray-600'
                      }`}>
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>

                      {/* Channel-Thumbnail */}
                      {channel.thumbnailPath ? (
                        <img
                          src={getImageUrl(channel.thumbnailPath)}
                          alt={channel.channelTitle}
                          className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                          <Youtube className="w-5 h-5 text-gray-400" />
                        </div>
                      )}

                      {/* Channel-Name */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {channel.channelTitle}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : null}
      </div>
      )}

      <TWModalFooter>
        {step === 'confirm' ? (
          <>
            <TWModalButton
              variant="cancel"
              onClick={handleBackToSelect}
              disabled={saving}
            >
              <ChevronLeft className="w-4 h-4" />
              Zurück
            </TWModalButton>
            <TWModalButton
              variant="primary"
              onClick={handleConfirmDowngrade}
              disabled={
                saving ||
                impactLoading ||
                overLimit ||
                (impact !== null && impact.affectedEntryCount > 0 && !understood)
              }
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Speichern...
                </>
              ) : (
                'Downgrade endgültig bestätigen'
              )}
            </TWModalButton>
          </>
        ) : (
          <>
            <TWModalButton
              variant="cancel"
              onClick={onClose}
              disabled={saving}
            >
              Abbrechen
            </TWModalButton>
            <TWModalButton
              variant="primary"
              onClick={handleProceedToConfirm}
              disabled={impactLoading || loading || overLimit}
            >
              {impactLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Vorschau wird geladen...
                </>
              ) : (
                'Weiter zur Bestätigung'
              )}
            </TWModalButton>
          </>
        )}
      </TWModalFooter>
    </TWModal>
  );
};

export default BlogDowngradeSelector;
